import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getUnreadCount
} from '../utils/notificationApi';

export function useNotifications() {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'ALL',
    type: 'ALL',
    since: null,
    limit: 50
  });

  // Fetch notifications with current filters
  const fetchNotifications = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch notifications for the current user (assuming they are a seller)
      const notificationsData = await getNotifications(currentUser.uid, filters);
      setNotifications(notificationsData);
      
      // Fetch unread count
      const unreadCountData = await getUnreadCount(currentUser.uid);
      setUnreadCount(unreadCountData);
      
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message || 'Failed to fetch notifications');
      // Set empty state on error to prevent app crashes
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, filters]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, status: 'READ', read_at: new Date() }
            : notification
        )
      );
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError(err.message || 'Failed to mark notification as read');
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      await markAllNotificationsAsRead(currentUser.uid);
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({
          ...notification,
          status: 'READ',
          read_at: new Date()
        }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError(err.message || 'Failed to mark all notifications as read');
    }
  }, [currentUser?.uid]);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Get filtered notifications
  const filteredNotifications = useMemo(() => {
    let filtered = [...notifications];

    // Apply status filter
    if (filters.status !== 'ALL') {
      filtered = filtered.filter(notification => notification.status === filters.status);
    }

    // Apply type filter
    if (filters.type !== 'ALL') {
      filtered = filtered.filter(notification => notification.type === filters.type);
    }

    // Apply time filter
    if (filters.since) {
      const sinceDate = new Date(filters.since);
      filtered = filtered.filter(notification => {
        const notificationDate = notification.created_at?.toDate?.() || new Date(notification.created_at);
        return notificationDate >= sinceDate;
      });
    }

    return filtered;
  }, [notifications, filters]);

  // Get notification statistics
  const stats = useMemo(() => {
    const total = notifications.length;
    const unread = notifications.filter(n => n.status === 'UNREAD').length;
    const read = total - unread;
    
    const byType = {
      NEW_ORDER: notifications.filter(n => n.type === 'NEW_ORDER').length,
      DUE_SOON: notifications.filter(n => n.type === 'DUE_SOON').length,
      OVERDUE: notifications.filter(n => n.type === 'OVERDUE').length,
      LOW_INVENTORY: notifications.filter(n => n.type === 'LOW_INVENTORY').length
    };

    return { total, unread, read, byType };
  }, [notifications]);

  // Temporarily disable real-time subscriptions to prevent Firestore errors
  // Will re-enable once we identify the root cause
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Use polling instead of real-time subscriptions for now
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [currentUser?.uid, fetchNotifications]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Note: Using polling instead of real-time subscriptions to prevent Firestore errors

  return {
    notifications: filteredNotifications,
    unreadCount,
    loading,
    error,
    filters,
    stats,
    markAsRead,
    markAllAsRead,
    updateFilters,
    refetch: fetchNotifications
  };
}
